import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Portal() {  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const [paying, setPaying] = useState(false);
  const [sp] = useSearchParams();
  const toastOnce = useRef(false);

  const load = () => {
    api.get(`/portal/${token}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  };
  useEffect(() => { load(); }, [token]);

  useEffect(() => {
    const v = sp.get("deposit");
    if (toastOnce.current || !v) return;
    toastOnce.current = true;
    setTimeout(() => {
      if (v === "success") {
        toast.success("Payment received — thank you!");
        load();
      } else {
        toast.info("Payment cancelled — no charge was made.");
      }
      window.history.replaceState({}, "", window.location.pathname);
    }, 50);
  }, [sp]);

  const payDeposit = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/portal/${token}/deposit`);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setPaying(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6 text-center">
        <p className="font-serif text-3xl">This page doesn't exist or has expired.</p>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#F9F9F8]" />;

  const todoForms = data.documents.filter((d) => d.kind === "form" && d.status !== "Completed");
  const contracts = data.documents.filter((d) => d.kind === "contract");
  const doneForms = data.documents.filter((d) => d.kind === "form" && d.status === "Completed");

  const submitForm = async (doc) => {
    setSubmitting(doc.id);
    try {
      const payload = { answers: doc.questions.map((q) => ({ label: q.label, value: answers[doc.id]?.[q.label] || "" })) };
      await api.post(`/portal/${token}/forms/${doc.id}`, payload);
      toast.success("Thank you — your answers were sent");
      setAnswers({ ...answers, [doc.id]: {} });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(null);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#1A1A1A]/15 py-3 text-base outline-none focus:border-[#1A1A1A] transition-colors";

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="client-portal">
      <header className="border-b border-[#1A1A1A]/8">
        <div className="max-w-2xl mx-auto px-6 py-8 text-center">
          <p className="font-serif text-3xl italic">Bahare</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7A7A] mt-2">Your Session Page</p>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-14 space-y-14">
        <Reveal>
          <p className="font-serif text-4xl mb-2">Hello, {data.client.first_name}.</p>
          <p className="text-sm text-[#4A4A4A]">
            Status: <span className="uppercase tracking-[0.15em] text-xs">{data.status}</span>
          </p>
        </Reveal>

        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-6">Your Session</p>
          <div className="grid grid-cols-2 gap-y-4 border-y border-[#1A1A1A]/10 py-6">
            {[["Session", data.session.category], ["Date", data.session.date], ["Time", data.session.time],
              ["Duration", data.session.duration],
              ["Location", data.session.location], ["Package", data.session.package], ["Price", data.session.price],
              ["Deposit", data.session.deposit], ["Balance", data.session.balance],
              ["Payment", data.session.payment_status]]
              .filter(([, v]) => v).map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">{k}</p>
                  <p className="text-sm mt-1">{v}</p>
                </div>
              ))}
          </div>
          <ChangeRequest token={token} requests={data.change_requests || []} onDone={load} />
          {data.payment?.has_session && data.payment?.deposit_amount > 0 && (
            <div className="mt-8 border border-[#1A1A1A]/10 p-6 bg-white/50" data-testid="portal-deposit-box">
              <p className="font-serif text-xl mb-2">Secure your date online</p>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-5">
                Pay ${data.payment.deposit_amount} now toward your session{data.payment.balance > data.payment.deposit_amount ? ` — the remaining $${data.payment.balance - data.payment.deposit_amount} is due on the day` : ""}. Secure card payment.
              </p>
              <button onClick={payDeposit} disabled={paying} data-testid="portal-pay-deposit"
                className="w-full bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
                {paying ? "Redirecting to checkout…" : `Pay Deposit — $${data.payment.deposit_amount}`}
              </button>
            </div>
          )}
          {data.payment?.has_session && data.payment?.balance === 0 && data.payment?.amount_paid > 0 && (
            <p className="mt-8 text-sm text-[#4A4A4A] border border-[#1A1A1A]/10 p-6 bg-white/50" data-testid="portal-paid-off">
              Paid in full — thank you.
            </p>
          )}
        </Reveal>

        {(todoForms.length > 0 || contracts.some((c) => c.status !== "Completed")) && (
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-6">To Do</p>
            <div className="space-y-8">
              {contracts.filter((c) => c.status !== "Completed").map((c) => (
                <div key={c.id} className="border border-[#1A1A1A]/10 p-6 bg-white/50" data-testid={`portal-todo-contract-${c.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <FileText size={16} strokeWidth={1.5} />
                    <p className="font-serif text-xl">{c.name}</p>
                  </div>
                  <p className="text-xs text-[#7A7A7A] mb-4">Status: {c.status} — please check your email for the DocuSign signing link.</p>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/documents/${c.id}/pdf`} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] uppercase tracking-[0.2em] border-b border-[#1A1A1A] pb-1">
                    Preview Agreement PDF
                  </a>
                </div>
              ))}
              {todoForms.map((f) => (
                <div key={f.id} className="border border-[#1A1A1A]/10 p-6 bg-white/50" data-testid={`portal-form-${f.id}`}>
                  <p className="font-serif text-xl mb-6">{f.name}</p>
                  <div className="space-y-6">
                    {f.questions.map((q) => (
                      <div key={q.label}>
                        <label className="block text-xs uppercase tracking-[0.18em] text-[#4A4A4A] mb-1">{q.label}</label>
                        <input value={answers[f.id]?.[q.label] || ""}
                          onChange={(e) => setAnswers({ ...answers, [f.id]: { ...(answers[f.id] || {}), [q.label]: e.target.value } })}
                          data-testid={`portal-answer-${f.id}-${q.label.slice(0, 20).replace(/\W+/g, "-")}`}
                          className={inputCls} />
                      </div>
                    ))}
                  </div>
                  <button onClick={() => submitForm(f)} disabled={submitting === f.id} data-testid={`portal-submit-${f.id}`}
                    className="mt-8 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] px-8 py-3.5 rounded-full hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
                    {submitting === f.id ? "Sending…" : "Submit Answers"}
                  </button>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        {data.gallery && data.gallery.length > 0 && (
          <Reveal>
            <div className="flex items-center justify-between mb-6">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Your Photos</p>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/gallery.zip`}
                data-testid="portal-download-all" download
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[10px] uppercase tracking-[0.2em] px-6 py-3 rounded-full hover:bg-[#4A4A4A] transition-colors">
                Download All (ZIP)
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="portal-gallery">
              {data.gallery.map((p) => (
                <a key={p.id} href={p.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url}
                  target="_blank" rel="noopener noreferrer" data-testid={`portal-gallery-${p.id}`}
                  className="block overflow-hidden bg-[#EFEFEC] group">
                  <img src={p.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url}
                    alt={p.caption || "Your session photo"} loading="lazy"
                    className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700" />
                </a>
              ))}
            </div>
            <p className="text-xs text-[#7A7A7A] mt-3">Tap any photo to open the full-resolution version.</p>
          </Reveal>
        )}

        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-6">Documents</p>
          <div className="space-y-3">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between border-b border-[#1A1A1A]/8 pb-3" data-testid={`portal-doc-${d.id}`}>
                <div>
                  <p className="text-sm">{d.name}</p>
                  <p className="text-xs text-[#7A7A7A]">
                    {d.status}{d.signed_at && ` · signed ${new Date(d.signed_at).toLocaleDateString()}`}
                  </p>
                </div>
                {d.has_pdf && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/documents/${d.id}/pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid={`portal-doc-view-${d.id}`} className="text-[11px] uppercase tracking-[0.2em] border-b border-[#1A1A1A] pb-0.5">
                    {d.signed ? "Signed PDF" : "View PDF"}
                  </a>
                )}
              </div>
            ))}
            {data.documents.length === 0 && <p className="text-sm text-[#7A7A7A]">No documents yet.</p>}
          </div>
        </Reveal>

        {doneForms.length > 0 && (
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-4">Completed</p>
            {doneForms.map((d) => (
              <p key={d.id} className="flex items-center gap-2 text-sm text-[#4A4A4A] py-1">
                <Check size={14} className="text-green-700" /> {d.name}
              </p>
            ))}
          </Reveal>
        )}

        <p className="text-center text-[10px] uppercase tracking-[0.25em] text-[#B5B5B0] pt-10">Bahare — Photographer · Realtor · Educator</p>
      </main>
    </div>
  );
}

function ChangeRequest({ token, requests, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ requested_date: "", requested_time: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/portal/${token}/change-request`, form);
      toast.success("Change request sent");
      setOpen(false);
      setForm({ requested_date: "", requested_time: "", message: "" });
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#1A1A1A]/15 py-3 text-base outline-none focus:border-[#1A1A1A] transition-colors";

  return (
    <div className="mt-8" data-testid="change-request-section">
      {requests.length > 0 && (
        <div className="mb-6 space-y-2">
          {requests.map((r) => (
            <p key={r.id} className="text-xs text-[#4A4A4A] flex items-center gap-2" data-testid={`change-request-${r.id}`}>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] ${r.status === "Approved" ? "bg-green-50 text-green-700" : r.status === "Declined" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>{r.status}</span>
              Change to {r.requested_date}{r.requested_time ? ` · ${r.requested_time}` : ""}
            </p>
          ))}
        </div>
      )}
      {open ? (
        <form onSubmit={submit} className="border border-[#1A1A1A]/10 p-6 bg-white/50 space-y-5" data-testid="change-request-form">
          <p className="font-serif text-xl">Request a different time</p>
          <div className="grid grid-cols-2 gap-5">
            <input type="date" required value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
              data-testid="change-request-date" className={inputCls} />
            <input placeholder="Preferred time" value={form.requested_time} onChange={(e) => setForm({ ...form, requested_time: e.target.value })}
              data-testid="change-request-time" className={inputCls} />
          </div>
          <input placeholder="Anything I should know? (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="change-request-message" className={inputCls} />
          <div className="flex gap-3">
            <button type="submit" disabled={loading} data-testid="change-request-submit"
              className="bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] px-8 py-3.5 rounded-full hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
              {loading ? "Sending…" : "Send Request"}
            </button>
            <button type="button" onClick={() => setOpen(false)}
              className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A] hover:text-[#1A1A1A] px-3">Cancel</button>
          </div>
        </form>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="change-request-open"
          className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A] hover:text-[#1A1A1A] border-b border-[#1A1A1A]/20 pb-1 transition-colors">
          Need a different date or time?
        </button>
      )}
    </div>
  );
}
